import React, { useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { ExportButton } from './action-buttons/export-button';
import { ImportButton } from './action-buttons/import-button/import-button';
import { AddPurchaseOrderButton } from './add-purchase-order-button';
import { PurchaseOrderEmptyState } from './components/purchase-order-empty-state/purchase-order-empty-state';
import { PurchaseOrderFilterBar } from './filter-bar';
import { PurchaseOrderGrid } from './grid/purchase-orders-grid';
import { messages } from './messages';
import { PurchaseOrderPdfDropdown } from './purchase-order-page/purchase-order-pdf-dropdown';
import { PurchaseOrdersPagingWrapper } from './purchase-orders-paging-wrapper';
import style from '../../grid-page.module.less';
import { GridHeaderWithOverflow } from '../../../components/grid-header/grid-header-with-overflow';
import { SellerFeatures } from '../../../enums/seller-features';
import { useEnablePurchaseOrderCsvImport } from '../../../feature-flags/hooks/use-enable-purchase-order-csv-import';
import { useIsSellerFeatureEnabled } from '../../../hooks/use-is-seller-feature-enabled';
import { usePurchaseOrdersGridContext } from './purchase-orders-grid-context';

const label = <FormattedMessage {...messages.purchaseOrderGridLabel} />;
const headerId = 'purchaseOrderGridHeader';

/**
 * Purchase Orders List Page component
 * Displays a list of purchase orders in a data grid or empty state based on feature availability
 * @returns {JSX.Element} The purchase orders list page
 */
export const PurchaseOrdersListPage = () => {
  const { data, isLoading, refreshPurchaseOrders, totalRecords, selectedIds, setSelectedIds } = usePurchaseOrdersGridContext();
  const isInventoryReorderingEnabled = useIsSellerFeatureEnabled(SellerFeatures.InventoryReordering);
  const isPurchaseOrderCsvImportEnabled = useEnablePurchaseOrderCsvImport();

  const buttons = useMemo(
    () =>
      [
        <AddPurchaseOrderButton key={headerId} disabled={!isInventoryReorderingEnabled} refreshPurchaseOrders={refreshPurchaseOrders} />,
        <PurchaseOrderPdfDropdown key='pdf-dropdown' data={data} disabled={!isInventoryReorderingEnabled} purchaseOrderIds={selectedIds} />,
        isPurchaseOrderCsvImportEnabled ? (
          <ImportButton key='import-button' disabled={!isInventoryReorderingEnabled} refreshPurchaseOrders={refreshPurchaseOrders} />
        ) : null,
        <ExportButton key='export-button' disabled={!isInventoryReorderingEnabled} />,
      ].filter(Boolean),
    [data, refreshPurchaseOrders, selectedIds, isInventoryReorderingEnabled, isPurchaseOrderCsvImportEnabled],
  );

  const view = useMemo(() => ({ id: headerId, buttons, label }), [buttons]);

  if (!isInventoryReorderingEnabled) {
    return <PurchaseOrderEmptyState view={view} />;
  }

  return (
    <div className={style['main-content']}>
      <div className={style['grid-header']}>
        <GridHeaderWithOverflow view={view} />
      </div>
      <div className={classnames(style['edit-grid-wrap'], style['edit-grid-wrap-no-filters'])}>
        <div className={style['grid-filter-wrap']}>
          <PurchaseOrderFilterBar />
        </div>
      </div>
      <div className={style['grid-content']}>
        <div className={classnames(style['grid-and-footer'], style['no-sidebar'])}>
          <PurchaseOrderGrid
            data={data}
            isLoading={isLoading}
            onSelectedChanged={setSelectedIds}
            refreshPurchaseOrders={refreshPurchaseOrders}
            selected={selectedIds}
          />
          <PurchaseOrdersPagingWrapper totalRecords={totalRecords} />
        </div>
      </div>
    </div>
  );
};
